import React, { useEffect, useState, useContext, useMemo } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import api from "../services/api";
import { SurveyContext } from "../context/SurveyContext";
import { AuthContext } from "../context/AuthContext";
import Message from "./Message";
import MentionInput from "./MentionInput";
import EmojiPicker from "./EmojiPicker";
import { FaSpinner, FaArrowLeft, FaSmile } from "react-icons/fa";
import { getProfilePicture } from "../utils/avatar";

const REPLY_MAX_CHARS = 280;

const MessageDetail = () => {
  const { id } = useParams();
  const [search] = useSearchParams();
  const { condition: ctxCondition, updateCondition } = useContext(SurveyContext);
  const { user } = useContext(AuthContext);
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [replyContent, setReplyContent] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showEmojis, setShowEmojis] = useState(false);
  const navigate = useNavigate();
  const condition = search.get("condition") || ctxCondition;
  const highlightCommentId = search.get("commentId") || null;

  const fetchMessage = async () => {
    try {
      if (!condition) return;
      setLoading(true);
      const { data } = await api.get(`/messages/${id}`, { params: { condition } });
      setMessage(data);
      if (!ctxCondition) updateCondition(condition);
    } catch (e) {
      console.error("Failed to load message:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessage();
  }, [id, condition, highlightCommentId]);

  const handleInteraction = async (type, targetId, messageId, count) => {
    // Refresh the message to get updated data
    fetchMessage();
  };

  // Append an emoji to the reply, respecting the reply length cap.
  const insertReplyEmoji = (emoji) => {
    setReplyContent((prev) =>
      (prev + emoji).length <= REPLY_MAX_CHARS ? prev + emoji : prev
    );
  };

  const handleSubmitReply = async () => {
    if (!replyContent.trim() || submitting) return;

    setSubmitting(true);
    try {
      await api.post("/comments", {
        messageId: id,
        content: replyContent.trim(),
        condition,
      });
      setReplyContent("");
      // Refresh the message to show the new comment
      fetchMessage();
    } catch (error) {
      console.error("Error posting reply:", error);
      alert("Failed to post reply. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Build list of mentionable users (post author + all commenters)
  const mentionableUsers = useMemo(() => {
    if (!message) return [];

    const users = [];
    const seen = new Set();

    // Add post author
    if (message.author?.username && !seen.has(message.author.username)) {
      users.push({
        username: message.author.username,
        _id: message.author._id,
      });
      seen.add(message.author.username);
    }

    // Add all commenters
    if (message.comments) {
      message.comments.forEach((comment) => {
        if (comment.author?.username && !seen.has(comment.author.username)) {
          users.push({
            username: comment.author.username,
            _id: comment.author._id,
          });
          seen.add(comment.author.username);
        }
      });
    }

    // Remove current user from mention list
    return users.filter((u) => u.username !== user?.username);
  }, [message, user?.username]);

  if (loading) {
    return (
      <div className="min-h-screen bg-spiders-bg flex items-center justify-center">
        <div className="flex items-center space-x-3 text-spiders-text-muted">
          <FaSpinner className="animate-spin text-xl" />
          <span>Loading post...</span>
        </div>
      </div>
    );
  }

  if (!message) {
    return (
      <div className="min-h-screen bg-spiders-bg flex items-center justify-center">
        <div className="text-center">
          <p className="text-spiders-text-muted text-lg mb-4">Post not found</p>
          <button
            onClick={() => navigate(-1)}
            className="spiders-btn-primary"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-spiders-bg">
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-4">
        {/* Header */}
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-full hover:bg-spiders-bg-elevated/50 text-spiders-text-primary transition-colors"
          >
            <FaArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-xl font-bold text-spiders-text-primary">Post</h1>
        </div>

        {/* Main Post Card */}
        <div className="bg-spiders-bg-surface rounded-2xl overflow-hidden">
          <Message
            message={message}
            showAllByDefault={true}
            onInteraction={handleInteraction}
            highlightCommentId={highlightCommentId}
          />
        </div>

        {/* Reply Input Card */}
        <div className="bg-spiders-bg-surface rounded-2xl p-4">
          <div className="flex items-start space-x-3">
            <img
              src={getProfilePicture(user?.username || "user", user?.avatarSeed)}
              alt={user?.username}
              className="w-10 h-10 rounded-full flex-shrink-0"
            />
            <div className="flex-1">
              <p className="text-spiders-text-muted text-sm mb-2">
                Replying to <span className="text-spiders-primary-400">@{message.author?.username}</span>
              </p>
              <MentionInput
                value={replyContent}
                onChange={setReplyContent}
                placeholder="Post your reply... (type @ to mention someone)"
                rows={3}
                className="text-base"
                mentionableUsers={mentionableUsers}
                disabled={submitting}
              />
              <div className="flex items-center justify-between mt-3">
                <button
                  type="button"
                  onClick={() => setShowEmojis(prev => !prev)}
                  className={`p-2 rounded-full transition-colors ${
                    showEmojis
                      ? "text-spiders-accent-400 bg-spiders-accent-400/10"
                      : "text-spiders-text-muted hover:text-spiders-text-secondary hover:bg-spiders-bg-surface"
                  }`}
                  title="Add emoji"
                >
                  <FaSmile className="w-4 h-4" />
                </button>
                <button
                  className="spiders-btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleSubmitReply}
                  disabled={!replyContent.trim() || submitting}
                >
                  {submitting ? (
                    <FaSpinner className="animate-spin w-4 h-4" />
                  ) : (
                    "Reply"
                  )}
                </button>
              </div>
              {showEmojis && (
                <EmojiPicker
                  onSelect={insertReplyEmoji}
                  onClose={() => setShowEmojis(false)}
                />
              )}
            </div>
          </div>
        </div>

        {/* Thread indicator if this is part of a conversation */}
        {message.comments && message.comments.length > 0 && (
          <div className="bg-spiders-bg-surface rounded-2xl px-4 py-3">
            <h2 className="text-spiders-text-primary font-bold">
              {message.comments.length} {message.comments.length === 1 ? 'Reply' : 'Replies'}
            </h2>
          </div>
        )}
      </div>
    </div>
  );
};

export default MessageDetail;
